






















































































































import type { MergeValues } from '@getaccept/editor-lib';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import ContentBlockContainer from '@getaccept/lib-shared/src/components/ContentBlockContainer.vue';
import FloatingComments from '@getaccept/lib-shared/src/contextual-comments/components/FloatingComments.vue';
import FloatingCommentsLoader from '@getaccept/lib-shared/src/contextual-comments/components/FloatingCommentsLoader.vue';
import { FloatingCommentsHelper } from '@getaccept/lib-shared/src/contextual-comments/helpers/floating-comments.helper';
import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import type { CommentEditorSelection } from '@getaccept/lib-shared/src/contextual-comments/types/comment-selection';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-comments/types/create-reply-input';
import type { CurrentUser } from '@getaccept/lib-shared/src/contextual-comments/types/current-user';
import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { DocumentType } from '@getaccept/lib-shared/src/enums/document-type';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Entity as EntityShared } from '@getaccept/lib-shared/src/types/entity';
import type { ManuallySignedPage } from '@getaccept/lib-shared/src/types/manually-signed-page';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { PusherEvent } from '@getaccept/pusher';
import debounce from 'debounce';
import elementResizeDetectorMaker from 'element-resize-detector';
import type { Channel } from 'pusher-js';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import type {
  CommentUpdatedPayload,
  EditAfterSendPayload,
} from '@getaccept/lib-shared/src/pusher/contextual-commenting/document-channel-payloads';
import TrackingService from '../../api/tracking/services/tracking.service';
import DocumentAttachments from '../../documents/components/DocumentAttachments.vue';
import DocumentHeader from '../../documents/components/DocumentHeader.vue';
import { pusherClient } from '../../pusher';
import { useContextualComments } from '../composables/contextual-comments.composable';
import type { PageElement } from '../types/page-element';
import { TrackingHelper } from '../../tracking/helpers/tracking.helper';
import PoweredByButtonMobile from './PoweredByButtonMobile.vue';

@Component({
  components: {
    DocumentHeader,
    ContentBlockContainer,
    DocumentAttachments,
    PoweredByButtonMobile,
    FloatingComments,
    FloatingCommentsLoader,
  },
})
export default class DocumentContainer extends Vue {
  @Prop() document!: SigningPageDocument;
  @Prop() authId: string;
  @Prop() token: { jwt: string; exp: number };
  @Prop() editorRecipientInput: EditorRecipientInput[];
  @Prop() documentPages!: DocumentPage[];
  @Prop() manuallySignedPages: ManuallySignedPage[];
  @Prop() pageWidth: number;
  @Prop() recipient: SigningPageRecipient;
  @Prop() entity: Entity;
  @Prop() theme: SigningTheme;
  @Prop() videoThumbnail?: string;
  @Prop() videoUrl?: string;
  @Prop() reminderExpirationDays: number;
  @Prop() focusedRequiredId?: string;
  @Prop() uploadingAttachmentIds: string[];
  @Prop() isPreview!: boolean;
  @Prop() requiredStepsLeft!: boolean;
  @Prop() attachments: DocumentAttachment[];
  @Prop() emptyBottomBar: boolean;
  @Prop() initials: string;
  @Prop() verifiedInitialFieldIds: string[];
  @Prop() partiallySignedFields: PartiallySignedEvent[];
  @Prop() trackingDisabled: boolean;
  @Prop() documentPusherChannel: Channel;
  @Prop() recipientPusherChannel: Channel;
  @Prop({ default: false }) showContextualComments: boolean;

  elementResizeDetector = elementResizeDetectorMaker();
  scrollMargin = 20;
  contextualCommentingDisabled = false;
  dimensionsUpdating = false;
  commentsComposable = useContextualComments();

  get isSigned(): boolean {
    return this.document.status === DocumentStatus.Signed;
  }

  get hasAttachments(): boolean {
    return !!this.attachments.length;
  }

  get allRecipients(): SigningPageRecipient[] {
    return this.document.recipients;
  }

  get currentUser(): CurrentUser {
    return {
      recipientId: this.recipient.id,
      name: this.recipient.fullName,
      thumbUrl: this.recipient.thumbUrl,
    };
  }

  get hasContent(): boolean {
    return (
      !!this.documentPages?.length || !!this.manuallySignedPages?.length || this.hasAttachments
    );
  }

  get isTemplate(): boolean {
    return this.document.type === DocumentType.Template;
  }

  get visibleDocumentContentPageElements(): PageElement[] {
    const contentBlockContainers: ContentBlockContainer[] = (this.$refs.pages ||
      []) as ContentBlockContainer[];

    const filteredTypes: DocumentPageType[] = [
      DocumentPageType.DocumentContent,
      DocumentPageType.ContentLink,
      DocumentPageType.Editor,
    ];

    return contentBlockContainers
      .filter(
        ({ page: { pageType }, isVisible }: ContentBlockContainer) =>
          filteredTypes.includes(pageType) && isVisible
      )
      .map(({ page: { pageType, id }, $refs }: ContentBlockContainer) => ({
        id,
        element: $refs.page as HTMLElement,
        type: pageType,
      }));
  }

  get documentManuallySigned(): boolean {
    return !!this.document?.manuallySignedPages?.length;
  }

  get mergeValues(): MergeValues {
    const {
      value,
      name,
      sendDate,
      uniqueId,
      user,
      recipients,
      expirationDate,
    }: SigningPageDocument = this.document;

    return {
      document: {
        value,
        name,
        sendDate,
        uniqueId,
        expirationDate,
      },
      sender: user,
      recipients,
      entity: this.entity as EntityShared,
    };
  }

  async trackLinkFieldClick(id: string) {
    if (this.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackFieldClick(id);
    } catch {
      console.log(`Failed to track click for field with id ${id}`);
    }
  }

  get comments(): Comment[] {
    return this.commentsComposable.comments.value;
  }

  get commentsLoaded(): boolean {
    return this.commentsComposable.loaded.value;
  }

  get selectedCommentId(): string {
    return this.commentsComposable.selectedCommentId.value;
  }

  commentsReady = false;

  @Watch('comments.length')
  async watchingComments() {
    this.commentsReady = false;
    await this.$nextTick();
    this.commentsReady = true;
  }

  @Watch('document', { immediate: true })
  @Watch('comments.length', { immediate: true })
  updateDetachedComments() {
    this.commentsComposable.updateDetachedFromVersion(this.document);
  }

  get showComments() {
    return !!this.sortedComments.length && this.showContextualComments && this.commentsReady;
  }

  get pageIds(): string[] {
    return this.documentPages.length ? this.documentPages.map((page: DocumentPage) => page.id) : [];
  }

  get sortedComments(): Comment[] {
    return this.comments
      .filter((comment: Comment) => !!comment.editorSelection)
      .sort(FloatingCommentsHelper.sortCommentsByEditorTextSelection)
      .sort((a: Comment, b: Comment) =>
        FloatingCommentsHelper.sortCommentsByPageOrder(a, b, this.pageIds)
      );
  }

  filterComments(comments: Comment[], pageId: string) {
    return comments
      .filter((comment: Comment) => !!comment.editorSelection)
      .filter((comment: Comment) => comment.pageId === pageId);
  }

  async handleCreateComment(editorSelection: CommentEditorSelection, pageId: string) {
    const createdComment = await this.commentsComposable.createComment(editorSelection, pageId, {
      userId: null,
      recipientId: this.recipient.id,
    });

    if (!createdComment) {
      return;
    }

    this.commentsComposable.selectComment(createdComment.id, this.recipient.id);
  }

  handleCreateReply(payload: CreateReplyInput) {
    this.commentsComposable.createReply(payload, this.recipient.id);
  }

  async trackLinkBlockClick(id: string) {
    if (this.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackContentLinkBlockClick(id);
    } catch (_) {
      console.log(`Failed to track click for content-link with id ${id}`);
    }
  }

  distanceToMiddle(page: PageElement): number {
    const position: number =
      page.element.offsetHeight / 2 + page.element.getBoundingClientRect().top;
    const middle: number = window.innerHeight / 2;
    return Math.abs(middle - position);
  }

  calculatePagesInViewPort(pages: PageElement[]): string[] {
    return pages
      .filter((page: PageElement) => TrackingHelper.isPageInFocus(page))
      .map((page: PageElement) => page.id);
  }

  calculateCurrentPages(pages: PageElement[]) {
    if (!pages.length) {
      return;
    }
    const visiblePages: string[] = this.calculatePagesInViewPort(pages);
    this.$emit('currentPages', visiblePages);
  }

  dimensionsChanged(element: HTMLElement) {
    this.$emit('updatePageWidth', element.offsetWidth);
    this.dimensionsUpdating = false;
  }

  debounceEmitDimensionsChanged: (element: HTMLElement) => void = debounce(
    this.dimensionsChanged,
    300
  );

  debounceCurrentDocumentPages: (pages: PageElement[]) => void = debounce(
    this.calculateCurrentPages,
    200
  );

  addContextualCommentingListeners() {
    pusherClient.listen(
      this.documentPusherChannel,
      PusherEvent.EditAfterSend,
      (payload: EditAfterSendPayload) => {
        this.contextualCommentingDisabled = payload.editAfterSendIsActive;
      }
    );
    pusherClient.listen(
      this.documentPusherChannel,
      PusherEvent.ContextualCommentReplyCreated,
      this.commentsComposable.onReplyCreated
    );
    pusherClient.listen(
      this.documentPusherChannel,
      PusherEvent.ContextualCommentUpdated,
      (payload: CommentUpdatedPayload) =>
        this.commentsComposable.onCommentUpdated(this.document.id, payload)
    );
    pusherClient.listen(
      this.documentPusherChannel,
      PusherEvent.ContextualCommentRead,
      this.commentsComposable.onCommentRead
    );
    pusherClient.listen(
      this.documentPusherChannel,
      PusherEvent.ContextualCommentReplyRead,
      this.commentsComposable.onReplyRead
    );
    pusherClient.listen(
      this.recipientPusherChannel,
      PusherEvent.ContextualCommentCreated,
      this.commentsComposable.onCommentCreated
    );
    pusherClient.listen(
      this.recipientPusherChannel,
      PusherEvent.ContextualCommentEditorSelectionCreated,
      this.commentsComposable.onEditorSelectionCreated
    );
    pusherClient.listen(
      this.recipientPusherChannel,
      PusherEvent.ContextualCommentUpdated,
      this.commentsComposable.onRecipientCommentUpdated
    );
  }

  mounted() {
    this.elementResizeDetector.listenTo(this.$refs.pagesContainer, (element: HTMLElement) => {
      if (element.offsetWidth !== this.pageWidth) {
        this.dimensionsUpdating = true;
      }
      this.debounceEmitDimensionsChanged(element);
      this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements);
    });

    window.addEventListener(
      'scroll',
      () => this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements),
      { passive: true }
    );
    if (this.showContextualComments) {
      if (!this.commentsLoaded) {
        this.commentsComposable.loadComments();
      }
      this.addContextualCommentingListeners();
    }
  }

  beforeDestroy() {
    this.elementResizeDetector.removeListener(this.$refs.pagesContainer);
    window.removeEventListener('scroll', () =>
      this.debounceCurrentDocumentPages(this.visibleDocumentContentPageElements)
    );
  }
}
